import { Store as VuexStore } from 'vuex';
import { StateInterface } from 'src/store';
import {
  Statistics,
  Distribution,
  Postings,
  Boards,
  BatchActions,
  // Users,
  // Tasks,
} from 'src/api';

export namespace Types {
  export type StatPoint = Statistics.Types.StatPoint;
  export type StatIncrementValue = Statistics.Types.StatIncrementValue;
  export type Execution = Distribution.Types.Execution;
  export type ErrorMessageData = {
    message: string;
    error: string;
    stack?: string;
  };

  export type MessageData =
    | StatIncrementValue
    | Execution[]
    | StatPoint[]
    | ErrorMessageData
    | Record<string, unknown>
    | null;
  // Posting?

  export interface Message {
    id?: string;
    service: string;
    action: string;
    data?: MessageData;
  }

  export interface ErrorMessage {
    message: string;
    connectionId: string;
    requestId: string;
  }

  export interface MessageEvent extends Event {
    data: string;
  }

  export class MessageError extends Error {
    public error: string;
    constructor(data: ErrorMessageData) {
      super(data.message);
      this.error = data.error;
      this.stack = data.stack;
    }
  }
}

function getCurrentBoardId() {
  const matches = document.location.pathname.match(/\/boards\/(\d+)/);
  return matches && matches.index === 0 ? parseInt(matches[1]) : null;
}

// export function filterStatisticsSet(stat: StatPoint[]) {
//   const boardId = getCurrentBoardId();
//   if (boardId) {
//     return stat.filter((s) => boardId === s.boardId);
//   }
//
//   return stat;
// }

export class WsHandler {
  private $store: VuexStore<StateInterface>;

  constructor(store: VuexStore<StateInterface>) {
    this.$store = store;
  }

  filterExecutions(executions: Types.Execution[]): Types.Execution[] {
    let data = executions;

    // filter new executions for non 'today' period
    const period = this.$store.state.distribution.dayOffset;
    if (period) {
      const todayDate = new Date().getDate();
      data = data.filter((e) => new Date(e.timestamp).getDate() === todayDate);
    }

    const boardId = getCurrentBoardId();
    if (boardId) {
      return data.filter((r) => r.boardId === boardId);
    }

    return data.filter((r) => r.error);
  }

  filterStatisticsValue(
    stat: Types.StatIncrementValue
  ): Types.StatIncrementValue | null {
    const boardId = getCurrentBoardId();
    if (boardId) {
      return boardId === stat.boardId ? stat : null;
    }
    return stat;
  }

  getStatisticsData(message: Types.Message) {
    switch (message.action) {
      case Statistics.EVENTS.addValidationSuccess: {
        // we don't have charts for that data and not handle it
        return null;
      }
      default: {
        if (message.action.startsWith('add')) {
          return this.filterStatisticsValue(
            message.data as Types.StatIncrementValue
          );
        }
        return message.data;
      }
    }
  }

  // todo: refactor to handle only expected messages, instead of filter not expected
  getDistributionData(message: Types.Message) {
    const handle = Distribution.EVENT_HANDLERS[message.action];
    if (handle) {
      return handle(message.data as Types.Execution[], this.$store, {
        boardId: getCurrentBoardId(),
      });
    }

    if (
      Distribution.SKIP_EVENTS.includes(message.action) ||
      Postings.SKIP_EVENTS.includes(message.action) ||
      Boards.SKIP_EVENTS.includes(message.action) ||
      BatchActions.SKIP_EVENTS.includes(message.action)
    ) {
      return null;
    }

    return message.data;
  }

  getMessageData(message: Types.Message) {
    switch (message.service) {
      // case Postings.SERVICE: // replaced to 'Distribution' bellow [1]
      // case Boards.SERVICE: // replaced to 'Distribution'
      case Distribution.SERVICE: {
        return this.getDistributionData(message);
      }
      case Statistics.SERVICE: {
        return this.getStatisticsData(message);
      }
      // case Users.SERVICE:
      // case Tasks.SERVICE:
      default:
        return message.data;
    }
  }

  // fix server messages format\data\incompatibility here
  prepareMessage(eventData: string): Types.Message {
    const message = JSON.parse(eventData) as Types.Message | Types.ErrorMessage;

    if ('message' in message) {
      // convert unhandled (why?) error message like
      // {message: 'Internal server error', connectionId: 'eioGvd4gCYcCETA=', requestId: 'eioH6FhtiYcFuTA='}
      return {
        service: 'message',
        action: 'error',
        data: {
          ...message,
          caption: `RequestId: ${message.requestId}`,
        },
      };
    }

    // all this handled by 'distribution' store [1]
    const service = [Postings.SERVICE, Boards.SERVICE].includes(message.service)
      ? Distribution.SERVICE
      : message.service;

    const fixedMessage = { ...message, service };

    return { ...fixedMessage, data: this.getMessageData(fixedMessage) };
  }

  getHandler() {
    return (eventName: string, event: Types.MessageEvent) => {
      // console.log('SOCKET EVENT', eventName, event.data);
      if (!eventName.startsWith('SOCKET_')) {
        return;
      }

      if (!event.data) {
        return this.$store.commit('socket/' + eventName.toUpperCase(), event);
      }

      const { action, service, data } = this.prepareMessage(event.data);

      if (service === 'message' && action === 'error') {
        return this.$store.dispatch('app/showServerError', data);
      }

      if (action && service && data !== null) {
        return this.$store.dispatch(`${service}/${action}`, data);
      }

      process.env.DEBUGGING &&
        console.log('ws message skipped', {
          service,
          action,
          data,
        });
    };
  }

  static create(store: VuexStore<StateInterface>) {
    return new WsHandler(store).getHandler();
  }
}
